import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordField } from "@/components/Atoms";
import { Plus, FolderOpen } from "@phosphor-icons/react";

export default function Technicians() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ cedula: "", nombre: "", email: "", especialidad: "", activo: true, password: "" });
    const [createdCreds, setCreatedCreds] = useState(null);
    const [error, setError] = useState("");
    const canCreate = user?.role === "comercial" || user?.role === "supervisor";

    const load = async () => { const r = await api.get("/technicians"); setItems(r.data); };
    useEffect(() => { load(); (async () => setEspecialidades((await api.get("/especialidades")).data))(); }, []);

    const create = async (e) => {
        e.preventDefault(); setError("");
        if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
        try {
            const r = await api.post("/technicians", form);
            setCreatedCreds({ ...r.data.login_credentials, nombre: r.data.nombre, password: form.password });
            setShowForm(false); setForm({ cedula: "", nombre: "", email: "", especialidad: "", activo: true, password: "" });
            load();
        } catch (err) { setError(err.response?.data?.detail || "Error"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="technicians-page">
            <div className="flex items-end justify-between mb-8 border-b border-zinc-200 pb-6">
                <div>
                    <div className="overline mb-2">Recurso humano técnico</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">Técnicos</h1>
                </div>
                {canCreate && (
                    <button onClick={() => setShowForm(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-new-tech">
                        <Plus size={16} weight="bold" /> {showForm ? "Cancelar" : "Nuevo técnico"}
                    </button>
                )}
            </div>

            {showForm && (
                <form onSubmit={create} className="border border-zinc-200 p-6 bg-zinc-50 mb-8 grid md:grid-cols-2 gap-4" data-testid="new-tech-form">
                    <Input label="Cédula *" value={form.cedula} onChange={v => setForm({...form, cedula: v})} testid="tec-cc" required />
                    <Input label="Nombre completo *" value={form.nombre} onChange={v => setForm({...form, nombre: v})} testid="tec-nom" required />
                    <Input label="Email *" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testid="tec-email" required />
                    <label>
                        <div className="overline mb-1.5">Especialidad *</div>
                        <select className="input-atia" value={form.especialidad} onChange={e => setForm({...form, especialidad: e.target.value})} required data-testid="tec-esp">
                            <option value="">— Selecciona —</option>
                            {especialidades.map(e => <option key={e} value={e}>{e}</option>)}
                        </select>
                    </label>
                    <div className="md:col-span-2 border-t border-zinc-200 pt-4">
                        <div className="overline mb-2" style={{ color: "#0F766E" }}>🔐 Credenciales de acceso del técnico</div>
                        <p className="text-xs text-zinc-600 mb-3">El técnico ingresará con su <strong>cédula</strong> ({form.cedula || "—"}) y esta contraseña.</p>
                        <Input label="Contraseña inicial * (mínimo 6 caracteres)" type="password" value={form.password} onChange={v => setForm({...form, password: v})} testid="tec-password" required />
                    </div>
                    {error && <div className="md:col-span-2 p-3 border border-red-300 bg-red-50 text-red-900 text-sm" data-testid="tec-error">{error}</div>}
                    <div className="md:col-span-2 flex justify-end">
                        <button type="submit" className="btn-primary" data-testid="submit-tech">Registrar técnico y generar acceso →</button>
                    </div>
                </form>
            )}

            {createdCreds && (
                <div className="border border-emerald-300 bg-emerald-50 p-6 mb-8" data-testid="tec-created-creds">
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>✅ Técnico registrado · Credenciales de acceso</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{createdCreds.nombre}</h3>
                    <p className="text-sm text-zinc-700 mb-3">Entrega estos datos al técnico:</p>
                    <div className="bg-white border border-zinc-200 p-4 font-mono text-sm space-y-1.5">
                        <div><span className="text-zinc-500">Identificador ({createdCreds.identifier_label}):</span> <strong>{createdCreds.identifier}</strong></div>
                        <div><span className="text-zinc-500">Contraseña:</span> <strong>{createdCreds.password}</strong></div>
                        <div><span className="text-zinc-500">Portal:</span> <strong>{window.location.origin}/login</strong></div>
                    </div>
                    <p className="text-xs text-zinc-600 mt-3">⚠️ Recuerda cargar el expediente HSE del técnico antes de asignarle visitas. Sin documentos críticos vigentes, el agendamiento será bloqueado.</p>
                    <button onClick={() => setCreatedCreds(null)} className="btn-secondary mt-4 text-sm" data-testid="tec-dismiss-creds">Entendido, cerrar</button>
                </div>
            )}

            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="techs-table">
                    <thead><tr><th>Cédula</th><th>Nombre</th><th>Email</th><th>Especialidad</th><th>Estado</th><th>Expediente HSE</th></tr></thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-zinc-500">Sin técnicos.</td></tr>}
                        {items.map(t => (
                            <tr key={t.tech_id} data-testid={`tech-row-${t.cedula}`}>
                                <td className="font-mono text-xs">{t.cedula}</td>
                                <td className="font-semibold">{t.nombre}</td>
                                <td className="text-zinc-600">{t.email}</td>
                                <td>{t.especialidad || "—"}</td>
                                <td>
                                    <span className={`badge-sev ${t.activo ? "sev-verde" : "sev-rojo"}`}>{t.activo ? "ACTIVO" : "INACTIVO"}</span>
                                </td>
                                <td>
                                    <Link to={`/technicians/${t.cedula}/documents`} className="text-xs font-semibold flex items-center gap-1" style={{ color: "#0F766E" }} data-testid={`tech-hse-${t.cedula}`}>
                                        <FolderOpen size={14} weight="fill" /> Ver expediente →
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, testid, type = "text", required }) {
    return (
        <label>
            <div className="overline mb-1.5">{label}</div>
            {type === "password" ? (
                <PasswordField value={value} onChange={onChange} testid={testid} required={required} autoComplete="new-password" />
            ) : (
                <input type={type} className="input-atia" value={value} onChange={e => onChange(e.target.value)} data-testid={testid} required={required} />
            )}
        </label>
    );
}
